import sessionHandling from "services/utils/notificationUtils";

const ENDPOINT = "payroll/configuration/pay-input/configuration/";

const errorText = (value) => {
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value.map(errorText).join(" ");
  if (value && typeof value === "object") {
    return Object.entries(value).map(([key, message]) => `${key}: ${errorText(message)}`).join(" ");
  }
  return "";
};

// Last config loaded or saved per domain + company + pay input, so the
// sidebar and Pay Inputs screens can use it straight away (no flash) while
// a fresh copy loads.
const configCache = new Map();
const cacheKey = (domain, companyId, payInputType) => `${domain}|${companyId}|${payInputType}`;
export const getCachedPayInputConfig = (domain, companyId, payInputType) =>
  configCache.get(cacheKey(domain, companyId, payInputType)) ?? null;

// For pay inputs with their own settings API (Reimbursements), so the
// sidebar can read their Enable switch from the same cache.
export const setCachedPayInputConfig = (domain, companyId, payInputType, config) =>
  configCache.set(cacheKey(domain, companyId, payInputType), config);

export const requestPayInputConfig = async (args) => {
  const result = await sendPayInputConfig(args);
  configCache.set(cacheKey(args.domain, args.companyId, args.payInputType), result.config);
  return result;
};

const sendPayInputConfig = async ({ domain, companyId, payInputType, config, signal }) => {
  const method = config ? (config.is_configured ? "PUT" : "POST") : "GET";
  const query = new URLSearchParams({ company_id: companyId, pay_input_type: payInputType });
  const url = `${domain.replace(/\/$/, "")}/${ENDPOINT}${method === "GET" ? `?${query}` : ""}`;
  const response = await fetch(url, {
    method,
    signal,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Token ${localStorage.getItem("token")}`,
    },
    ...(config ? { body: JSON.stringify({
      company_id: Number(companyId),
      pay_input_type: payInputType,
      is_enabled: config.enabled,
      payroll_cycle: { default_treatment: config.treatment },
      approval: { required: config.approvalRequired === "yes" },
      allowed_sources: Object.fromEntries(config.sourceOptions.map(({ value, comingSoon }) => [
        value, !comingSoon && value !== "ERP" && config.sources.includes(value),
      ])),
    }) } : {}),
  });
  if (response.status === 401) {
    localStorage.clear();
    window.location.href = "/auth/login";
    throw new Error("Your session has expired. Please sign in again.");
  }
  if (response.status === 409) sessionHandling();
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(errorText(body?.error || body?.detail || body) || `Request failed (${response.status}). Please try again.`);
  }
  const data = config ? body?.data : body;
  if (!data || data.pay_input_type !== payInputType) throw new Error("The server returned an invalid pay input configuration.");
  return { config: fromApiConfig(data), message: body?.message };
};

export const fromApiConfig = (data) => ({
  is_configured: data.is_configured,
  enabled: data.is_enabled,
  treatment: data.payroll_cycle.default_treatment,
  treatmentOptions: data.payroll_cycle.options,
  approvalRequired: data.approval.required ? "yes" : "no",
  // Leave gets the Approval card like Overtime / Shift Earnings even though
  // the server still reports is_configurable: false for LEAVE (user rule).
  approvalConfigurable: Boolean(data.approval.is_configurable) || data.pay_input_type === "LEAVE",
  sourceOptions: data.source_details.map((source) => ({
    value: source.source_type,
    label: source.label,
    comingSoon: !source.is_available || source.source_type === "ERP",
  })),
  sources: data.source_details.filter((source) => source.is_available && source.source_type !== "ERP" && data.allowed_sources[source.source_type]).map((source) => source.source_type),
});
